import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Minimize2, Play, Square } from "lucide-react";
import { apiBaseUrl, authHeaders, toUtcDate } from "../pages/dispatcher/shared";
import LoadingState from "../pages/dispatcher/LoadingState";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";

// Historical Idle Movement map -- the exact GPS breadcrumb trail a truck drove
// while there was NO load destination associated with the movement. Idle trail
// is visually PRIMARY (amber, one polyline per episode -- gaps/teleports/episode
// boundaries are NEVER bridged). Deadhead-to-next-pickup is secondary (dashed
// grey). A scrub timeline + truck icon lets you replay the movement point by
// point (same interaction as Route History's route monitor). No road-snapping.

const IDLE_COLOR = "#d97706";
const DEADHEAD_COLOR = "#94a3b8";

function truckIcon(label) {
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="background:#d97706;color:#fff;border:1px solid #0b1020;font-size:12px;font-weight:800;
        padding:3px 9px;border-radius:999px;white-space:nowrap;margin-bottom:3px;box-shadow:0 2px 8px rgba(0,0,0,.5);">${label || "truck"}</div>
      <svg width="48" height="48" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.5));">
        <rect x="1" y="9" width="14" height="8" rx="1.5" fill="#d97706" stroke="#0b1020" stroke-width="1.2"/>
        <path d="M15 11 h6 l3 4 v2 h-9 z" fill="#d97706" stroke="#0b1020" stroke-width="1.2"/>
        <circle cx="6" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
        <circle cx="20" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [110, 74], iconAnchor: [55, 60] });
}
function pinIcon(color, letter) {
  const html = `
    <svg width="26" height="34" viewBox="0 0 26 34" style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
      <path d="M13 0C5.8 0 0 5.8 0 13c0 9.7 13 21 13 21s13-11.3 13-21C26 5.8 20.2 0 13 0z" fill="${color}"/>
      <circle cx="13" cy="13" r="7" fill="#fff"/>
      <text x="13" y="17" font-size="10" font-weight="800" text-anchor="middle" fill="${color}">${letter}</text>
    </svg>`;
  return L.divIcon({ html, className: "", iconSize: [26, 34], iconAnchor: [13, 34] });
}

function MapFit({ points, fitKey }) {
  const map = useMap();
  const last = React.useRef(null);
  useEffect(() => {
    if (!points || points.length < 2) return;
    if (fitKey === last.current) return;
    last.current = fitKey;
    map.fitBounds(L.latLngBounds(points), { padding: [55, 55], maxZoom: 12 });
  }, [points, fitKey, map]);
  return null;
}

function fmtDur(s) { if (s == null) return "—"; const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; }
function fmtTs(iso) { return toUtcDate(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

export default function IdleRouteModal({ segId, unitNumber, driverName, laneLabel, onClose, themeClassName, themeVars }) {
  const tileUrl = useTileLayerUrl();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrubIndex, setScrubIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!segId) return;
    setLoading(true); setError(null);
    axios
      .get(`${apiBaseUrl}/fleet/idle-movement/trail`, { ...authHeaders(), params: { seg_id: segId } })
      .then((res) => {
        setData(res.data);
        const n = (res.data.idle_episodes || []).reduce((a, ep) => a + ep.length, 0);
        setScrubIndex(Math.max(0, n - 1));
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [segId]);

  const episodes = useMemo(() => data?.idle_episodes || [], [data]);
  const deadhead = data?.deadhead_trail || [];
  const m = data?.markers || {};
  const meta = data?.meta || {};

  // flat, time-ordered idle points across episodes (episodes are already sequential)
  const flat = useMemo(() => episodes.flatMap((ep, ei) => ep.map((p) => ({ ...p, ei }))), [episodes]);

  useEffect(() => {
    if (!playing || flat.length === 0) return;
    if (scrubIndex >= flat.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setScrubIndex((i) => Math.min(i + 1, flat.length - 1)), 350);
    return () => clearTimeout(t);
  }, [playing, scrubIndex, flat]);

  // visible points up to the scrubber, regrouped per episode (never bridged)
  const visByEp = useMemo(() => {
    const g = {};
    flat.slice(0, scrubIndex + 1).forEach((p) => { (g[p.ei] = g[p.ei] || []).push(p); });
    return g;
  }, [flat, scrubIndex]);
  const current = flat[scrubIndex] || null;

  const allPts = [
    ...flat.map((p) => [p.lat, p.lng]),
    ...deadhead.map((p) => [p.lat, p.lng]),
    ...[m.prev_delivery, m.next_pickup].filter(Boolean).map((x) => [x.lat, x.lng]),
  ].filter(([a, b]) => a != null && b != null);
  const center = allPts[0] || [39.5, -98.35];

  return createPortal(
    <div className={themeClassName}
      style={{ ...themeVars, position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}>
      <div style={{ width: "92vw", height: "88vh", background: "var(--card)", border: "1px solid var(--line)",
          borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
            Idle Movement route — Truck {unitNumber || "?"}
            <span style={{ fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>
              {driverName || "Unknown / Unattributed"}{laneLabel ? ` · ${laneLabel}` : ""}
            </span>
          </div>
          <button onClick={onClose}
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--ink)",
              background: "var(--line)", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>
            <Minimize2 size={13} /> Close
          </button>
        </div>

        {loading && <LoadingState label="Loading idle route…" />}
        {error && <div style={{ color: "#e0745d", padding: 20, fontSize: 13 }}>Couldn't load the idle route: {error}</div>}

        {!loading && !error && data && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: 8, fontSize: 12, color: "var(--muted)" }}>
              <span style={{ color: IDLE_COLOR, fontWeight: 700 }}>Idle {Math.round(meta.idle_miles ?? 0).toLocaleString()} mi</span>
              <span>Deadhead after {Math.round(meta.deadhead_miles ?? 0).toLocaleString()} mi</span>
              <span>{fmtDur(meta.duration_s)}</span>
              <span>{meta.gps_points ?? 0} GPS points</span>
              <span>{meta.episodes ?? 0} episode{meta.episodes === 1 ? "" : "s"}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto", flexWrap: "wrap" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ display: "inline-block", width: 16, height: 3, borderRadius: 2, background: IDLE_COLOR }} /> Idle movement
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ display: "inline-block", width: 16, height: 0, borderTop: `3px dashed ${DEADHEAD_COLOR}` }} /> Deadhead to pickup
                </span>
              </span>
            </div>

            <div style={{ flex: 1, minHeight: 0, borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)" }}>
              <MapContainer center={center} zoom={8} style={{ height: "100%", width: "100%" }}>
                {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
                <MapFit points={allPts} fitKey={segId} />

                {deadhead.length >= 2 && (
                  <Polyline positions={deadhead.map((p) => [p.lat, p.lng])}
                    pathOptions={{ color: DEADHEAD_COLOR, weight: 3, opacity: 0.75, dashArray: "8 7" }} />
                )}
                {Object.values(visByEp).map((pts, i) =>
                  pts.length >= 2 ? (
                    <Polyline key={i} positions={pts.map((p) => [p.lat, p.lng])} pathOptions={{ color: IDLE_COLOR, weight: 4 }} />
                  ) : null
                )}

                {m.prev_delivery && (
                  <Marker position={[m.prev_delivery.lat, m.prev_delivery.lng]} icon={pinIcon("#4cae6d", "A")}>
                    <Tooltip>Came from — previous delivery{m.prev_delivery.label ? ` — ${m.prev_delivery.label}` : ""}</Tooltip>
                  </Marker>
                )}
                {m.next_pickup && (
                  <Marker position={[m.next_pickup.lat, m.next_pickup.lng]} icon={pinIcon("#1d6fd8", "B")}>
                    <Tooltip>Heading to — next pickup{m.next_pickup.label ? ` — ${m.next_pickup.label}` : ""}</Tooltip>
                  </Marker>
                )}
                {current && (
                  <Marker position={[current.lat, current.lng]} icon={truckIcon(unitNumber)}>
                    <Tooltip>{fmtTs(current.recorded_at)}</Tooltip>
                  </Marker>
                )}
              </MapContainer>
            </div>

            {flat.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setPlaying((p) => !p)} title={playing ? "Pause" : "Replay the movement"}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30,
                    background: "var(--panel, var(--bg))", border: "1px solid var(--line)", borderRadius: 8, cursor: "pointer", color: "var(--ink)", flexShrink: 0 }}>
                  {playing ? <Square size={13} /> : <Play size={13} />}
                </button>
                <input type="range" min={0} max={Math.max(0, flat.length - 1)} value={scrubIndex}
                  onChange={(e) => { setPlaying(false); setScrubIndex(Number(e.target.value)); }}
                  style={{ flex: 1, accentColor: IDLE_COLOR, height: 6, cursor: "pointer" }} />
                <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 150, textAlign: "right" }}>
                  {current ? fmtTs(current.recorded_at) : "—"}
                </span>
              </div>
            )}
            {episodes.length === 0 && (
              <div style={{ color: "var(--muted)", padding: "10px 2px", fontSize: 12.5 }}>No GPS breadcrumbs recorded for this segment.</div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
